import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Formik } from 'formik'
import CurrencyInput from '../enterpriseSOW/CurrencyInput'
import PropTypes from 'prop-types'
import QuestHOC from './QuestHOC'
import {
  autosaveAfterFieldChange,
  queueAutosaveOnBlur,
  runQueuedFormikSave,
  FormikDirtyReporter,
} from './questAutosave'
import ExperienceFieldLabel from './ExperienceFieldLabel'
import ExperienceBadge from './ExperienceBadge'
import { ExperienceSelect } from '../form'
import { getProgramDatesRange } from '../../utils/date'
import {
  AUTO_DEDUCT_DAYS_OPTIONS,
  DEPOSIT_PERCENTAGE_OPTIONS,
  getAutoDeductDateFromStart,
  getAutoDeductDays,
  getDepositPercentage,
} from '../../utils/questPayment'

const PAYMENT_DEFAULT_HINT =
  'Default for this experience. Can be updated per participant while they enroll.'

const stripCurrency = (val) => {
  if (!val) return 0
  const numericValue = Number(String(val).replace(/[^0-9.]/g, ''))
  return isNaN(numericValue) ? 0 : numericValue
}

const getBalanceDueLabel = (days, startDate) => {
  if (!days) return 'With the deposit'
  const dueDate = getAutoDeductDateFromStart(startDate, days)
  const label = `${days} days before departure`
  return dueDate ? `${label} (${dueDate.format('D MMMM YYYY')})` : label
}

const buildPayload = (values) => ({
  investments: {
    standardRate: stripCurrency(values.standardRate),
    singleSupplement: stripCurrency(values.singleSupplement),
    defaultDepositPercentage: Number(values.defaultDepositPercentage),
    defaultAutoDeductDays: Number(values.defaultAutoDeductDays),
  },
})

const valuesFromQuest = (questDetail) => ({
  standardRate: questDetail?.investments?.standardRate || 0,
  singleSupplement: questDetail?.investments?.singleSupplement || 0,
  defaultDepositPercentage: String(
    getDepositPercentage(questDetail?.investments),
  ),
  defaultAutoDeductDays: String(getAutoDeductDays(questDetail?.investments)),
})

const QuestInvestment = ({
  questDetail,
  updateQuestDetails,
  isFormSubmitting,
  isSaving,
}) => {
  const busy = !!isFormSubmitting || !!isSaving
  const [initialValues, setInitialValues] = useState(() =>
    valuesFromQuest(questDetail),
  )
  const valuesRef = useRef(initialValues)

  useEffect(() => {
    const next = valuesFromQuest(questDetail)
    setInitialValues(next)
    valuesRef.current = next
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questDetail?._id, questDetail?.id])

  const { start: questStartDate } = getProgramDatesRange(questDetail)

  const balanceDueOptions = useMemo(
    () =>
      AUTO_DEDUCT_DAYS_OPTIONS.map((days) => ({
        value: String(days),
        label: getBalanceDueLabel(days, questStartDate),
      })),
    [questStartDate],
  )

  const depositOptions = useMemo(
    () =>
      DEPOSIT_PERCENTAGE_OPTIONS.map((opt) => ({
        value: String(opt.value),
        label: opt.label,
      })),
    [],
  )

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={async (values, { resetForm }) => {
        await runQueuedFormikSave({
          values,
          valuesRef,
          buildPayload,
          resetForm,
          setInitialValues,
          save: (payload) => updateQuestDetails(payload, { silent: true }),
        })
      }}>
      {(formik) => {
        const { handleSubmit, setFieldValue, dirty } = formik
        valuesRef.current = formik.values

        const fieldBlur = () => {
          queueAutosaveOnBlur(formik)
        }

        return (
          <form
            onSubmit={handleSubmit}
            className='investment-details-form'
            aria-busy={busy}>
            <FormikDirtyReporter id='setup-price' dirty={dirty} />
            <div className='experience-f-row'>
              <div className='experience-f'>
                <label className='experience-f-label'>
                  <ExperienceFieldLabel where={['pub']}>
                    Shared Room
                  </ExperienceFieldLabel>
                </label>
                <CurrencyInput
                  name='standardRate'
                  placeholder='Add price'
                  inputClasses='experience-f-control'
                  onBlur={fieldBlur}
                />
              </div>
              <div className='experience-f'>
                <label className='experience-f-label'>
                  <ExperienceFieldLabel where={['pub']}>
                    Single Occupancy Rate
                  </ExperienceFieldLabel>
                </label>
                <CurrencyInput
                  name='singleSupplement'
                  placeholder='Optional'
                  inputClasses='experience-f-control'
                  onBlur={fieldBlur}
                />
              </div>
            </div>

            <div className='experience-f-row'>
              <div className='experience-f'>
                <ExperienceSelect
                  name='defaultDepositPercentage'
                  label={
                    <>
                      Deposit{' '}
                      <ExperienceBadge title='Participant' variant='peach' />
                    </>
                  }
                  options={depositOptions}
                  placeholder='Select deposit'
                  isSearchable={false}
                  onChange={(e) => {
                    autosaveAfterFieldChange(
                      formik,
                      setFieldValue(
                        'defaultDepositPercentage',
                        e.target.value,
                        false,
                      ),
                    )
                  }}
                  onBlur={fieldBlur}
                />
                <p className='experience-f-hint text-dark'>
                  {PAYMENT_DEFAULT_HINT}
                </p>
              </div>
              <div className='experience-f'>
                <ExperienceSelect
                  name='defaultAutoDeductDays'
                  label={
                    <>
                      Balance due{' '}
                      <ExperienceBadge title='Participant' variant='peach' />
                    </>
                  }
                  options={balanceDueOptions}
                  placeholder='Select balance due'
                  isSearchable={false}
                  onChange={(e) => {
                    autosaveAfterFieldChange(
                      formik,
                      setFieldValue(
                        'defaultAutoDeductDays',
                        e.target.value,
                        false,
                      ),
                    )
                  }}
                  onBlur={fieldBlur}
                />
                <p className='experience-f-hint text-dark'>
                  {PAYMENT_DEFAULT_HINT}
                </p>
              </div>
            </div>
          </form>
        )
      }}
    </Formik>
  )
}

QuestInvestment.propTypes = {
  questDetail: PropTypes.shape({
    investments: PropTypes.shape({
      standardRate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      singleSupplement: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      defaultDepositPercentage: PropTypes.number,
      defaultAutoDeductDays: PropTypes.number,
    }),
  }),
  updateQuestDetails: PropTypes.func.isRequired,
  isFormSubmitting: PropTypes.bool,
  isSaving: PropTypes.bool,
}

export default QuestHOC(QuestInvestment)
